import _ from 'lodash';
import { sqItemRuntimeStore, sqScorecardStore, sqTrendScalingColumnStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { getMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import { getItemsInScalingTableRow } from '@/tableDefinitionEditor/scalingTable.api';
import { resolveScalingTableRowName } from '@/tableDefinitionEditor/scalingTable.utilities';
import { CSV_TYPES, ID_COLUMN_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { isItemTableCell } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { findItem } from '@/trendData/itemStore.utilities';
import { fetchAllItems, setTrendItemProps } from '@/trendData/trend.actions';
import { swapItemsInTrendStores } from '@/trendData/trendItemStores.actions';
import { DUMMY_ITEM_PREFIX } from '@/utilities/formula.constants';
import { randomUUID } from '@/utilities/guid.utilities';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { FilterTypeEnum } from '@seeqdev/api/model/ValueFilter';
import { SeeqNames } from '@seeqdev/constants';
import type { PushOption } from '@/core/flux.service';
import type { ScalingTableSwapRow } from '@/search/search.types';
import type { ItemTableCell, ProcessedTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ScalingColumnItem } from '@/trendData/trendData.types';

export async function addScalingColumn(
  column: ScalingColumnItem,
  props?: {
    sampleItemName?: string;
  },
  option?: PushOption,
) {
  if (!findItem(sqTrendScalingColumnStore.getState().items, column.id)) {
    const payload = {
      id: column.id,
      name: `Column: ${column.name} -- Sample Item: ${props?.sampleItemName}`,
      props: props ?? {},
    };
    sqTrendScalingColumnStore.actions.addScalingColumn(payload);

    setTrendItemProps(column.id, props, option);
  }
  return [];
}

export async function swapScalingTableRows(
  swapRow: { id: string; name: string },
  swapColumns: ScalingTableSwapRow[],
  allItemsCount: number,
  { skipFetch = false, onWarning }: { skipFetch?: boolean; onWarning?: () => void } = {},
): Promise<void> {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: swapColumns[0].tableId });
  const isScalingTableCSV = CSV_TYPES.includes(scalingTableData.tableType);
  const itemIdColumnName = isScalingTableCSV
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;
  const swapColumnsWithNames = swapColumns
    .map((column) => ({
      ...column,
      columnName:
        column.columnName ??
        scalingTableData.columnDefinitions.find((definition) => definition.id === column.columnId)?.columnName,
    }))
    .filter((column): column is ScalingTableSwapRow & { columnName: string } => !!column.columnName);
  const showNotPossibleWarning = () => {
    if (onWarning) {
      onWarning();
    } else {
      warnToast({
        messageKey: 'SCALING_ROW_REPLACE.NOT_POSSIBLE',
        messageParams: {
          SWAP_ROW_NAME: swapRow.name,
        },
      });
    }
  };

  if (!swapColumnsWithNames.length) {
    showNotPossibleWarning();
    return;
  }

  if (swapColumnsWithNames.some((column) => ID_COLUMN_TYPES.includes(column.columnName))) {
    swapColumnsWithNames
      .filter(({ columnName }) => ID_COLUMN_TYPES.includes(columnName))
      .forEach((column) => (column.columnName = itemIdColumnName));
  }

  const tableData = await getMaterializedTable(
    swapColumnsWithNames[0].tableId,
    swapColumnsWithNames.map((column) => column.columnName),
    swapColumnsWithNames.map((column) => ({
      uuidColumn: column.columnName,
      propertyNames: [
        SeeqNames.Properties.Name,
        SeeqNames.Properties.Type,
        SeeqNames.Properties.CreatedByModelInstance,
      ],
    })),
    undefined,
    undefined,
    undefined,
    {
      valueFilter: {
        filterType: FilterTypeEnum.EQUALS,
        columnName: itemIdColumnName,
        value: { value: swapRow.id },
      },
    },
  );

  const processedMaterializedTable = processMaterializedTable(
    tableData,
    scalingTableData.columnDefinitions.map((column) => {
      return {
        ...column,
        displayName: column.columnName,
        additionalProperties: [SeeqNames.Properties.CreatedByModelInstance],
      };
    }),
  );

  if (!processedMaterializedTable || !processedMaterializedTable.rows.length) {
    showNotPossibleWarning();
    return;
  }

  const materializedTableRows = processedMaterializedTable?.rows[0]
    .map((row, index) => ({
      ...row,
      columnName: processedMaterializedTable.headers[index].name,
    }))
    .filter((cell: ProcessedTableCell) => isItemTableCell(cell));

  const swaps = _.zipObject(
    swapColumnsWithNames.map((col) => col.id),
    swapColumnsWithNames.map((col) => {
      const matchedRow = materializedTableRows.find((row) => row.columnName === col.columnName);
      return matchedRow ? (matchedRow as ItemTableCell).uuid : `${DUMMY_ITEM_PREFIX}-${randomUUID()}`;
    }),
  );

  swapItemsInTrendStores({ swaps });
  _.forEach(swaps, (swappedInId) => sqItemRuntimeStore.actions.clearDataStatus(swappedInId));
  sqScorecardStore.actions.swapItems(swaps);
  // For each swap item, update the props to reflect the new row reference
  _.forEach(swapColumnsWithNames, (scalingRowItem) => {
    const { id, ...scalingRowItemPropsWithoutId } = scalingRowItem;
    const matchedRow = materializedTableRows.find((row) => row.columnName === scalingRowItem.columnName);
    const instanceId =
      matchedRow && isItemTableCell(matchedRow)
        ? matchedRow.fetchedProperties?.[SeeqNames.Properties.CreatedByModelInstance]
        : undefined;

    setTrendItemProps(swaps[scalingRowItem.id], {
      ...scalingRowItemPropsWithoutId,
      columnName: scalingRowItem.columnName,
      columnId:
        scalingRowItem.columnId ??
        scalingTableData.columnDefinitions.find((column) => column.columnName === scalingRowItem.columnName)?.id,
      instanceId,
      rowName: swapRow.name,
      rowId: swapRow.id,
      stringEnum: undefined,
      isStringSeries: undefined,
    });
  });
  successToast({
    messageKey: 'SWAPS.SUCCESS_V2',
    messageParams: {
      successfulSwapsCount: _.size(swaps),
      allItemsCount,
    },
  });

  if (!skipFetch) {
    fetchAllItems();
  }
}

export function setScalingColumnsUpdated(scalingColumnsUpdated: boolean) {
  flux.dispatch('TREND_SET_SCALING_COLUMNS_UPDATED', scalingColumnsUpdated);
}

export async function getScalingTableItemProps(tableId: string, rowId: string, itemId: string, columnId?: string) {
  const { materializedTableRow, navigationColumnCell, scalingTableDefinition } = await getItemsInScalingTableRow(
    tableId,
    rowId,
  );
  const isPlaceholder = itemId.startsWith(DUMMY_ITEM_PREFIX);

  const columnFromTable = scalingTableDefinition.columnDefinitions.find((column) => column.id === columnId);
  const columnFromMaterializedTable = materializedTableRow.find((cell) => cell.uuid === itemId);
  const column = columnId ? columnFromTable?.columnName : columnFromMaterializedTable?.columnName;

  if (!column) {
    return;
  }

  const itemAtCoordinates = materializedTableRow.find((cell) => cell.columnName === column);
  const correctItemAtCoordinates = itemAtCoordinates && itemAtCoordinates.uuid === itemId;

  const isCorrectItemOrPlaceholder = correctItemAtCoordinates || isPlaceholder;
  const rowName = isCorrectItemOrPlaceholder
    ? resolveScalingTableRowName(scalingTableDefinition, materializedTableRow, navigationColumnCell, rowId)
    : undefined;
  const tableName = isCorrectItemOrPlaceholder ? scalingTableDefinition.name : undefined;
  const columnName = isCorrectItemOrPlaceholder ? column : undefined;
  const columnIdProp =
    columnId ??
    scalingTableDefinition.columnDefinitions.find(
      (column) => column.columnName === columnFromMaterializedTable?.columnName,
    )?.id;

  if (isCorrectItemOrPlaceholder) {
    setTrendItemProps(itemId, {
      tableId,
      tableName,
      rowName,
      rowId,
      columnName,
      columnId: columnIdProp,
      previousTableId: undefined,
      previousRowId: undefined,
      previousColumnId: undefined,
    });
    return;
  }

  setTrendItemProps(itemId, {
    previousTableId: tableId,
    previousRowId: rowId,
    previousColumnId: columnIdProp,
    tableId: undefined,
    tableName: undefined,
    rowName: undefined,
    rowId: undefined,
    columnName: undefined,
    columnId: undefined,
  });
}
